// ROADS (Matt, 2026-09-25). Laid by the Frontier a hex at a time, upgraded
// by the tree all at once, and felt in two places: a unit on the road goes
// further for an order, and a village joined to the city by road sends more.
// Pure; `civ.roads` is a list of "q,r" keys. See DESIGN.md "ROADS".
import { hexDistanceBetween, isTown, key as hexKey } from './map';
import { modsOf } from './mods';

const NEIGHBOURS = [[1, 0], [1, -1], [0, -1], [-1, 0], [-1, 1], [0, 1]];

/** 0 before Paths; 1 track, 2 paved (Roads), 3 highway (Highways). */
export const roadLevel = (civ) => Math.max(0, modsOf(civ).roadLevel || 0);
export const roadsOf = (civ) => civ.roads || [];
/**
 * THE CITY IS WHAT THE WALLS ENCLOSE (playtest 12). It used to be everything
 * within two hexes of the square, a ring the map never drew: road could not
 * go "right outside the city door", and Ashby, just past the wall, had a road
 * nobody laid or could see. Matt: "we should have to build the roads, that's
 * too much of a freebie." An old save with no city keeps the old disc.
 */
export const inCity = (civ, q, r) => (civ.city?.hexes ? civ.city.hexes.some(([a, b]) => a === q && b === r) : isTown(q, r));
/** The city is the hub every road runs from, so its hexes count as road. */
export const onRoad = (civ, q, r) => inCity(civ, q, r) || roadsOf(civ).includes(hexKey(q, r));
export const hasLaidRoad = (civ, q, r) => roadsOf(civ).includes(hexKey(q, r));
const villageOn = (civ, q, r) => (civ.villages || []).some((v) => v.q === q && v.r === r && !v.taken);

/**
 * May road go down here: not already road, not in the city, and next to the
 * city, a road, or one of your villages - so a road can be started at either
 * end (playtest 12, Brian: lay roads "from towns as well as from the city").
 */
export const canLayRoad = (civ, q, r) => {
  if (roadLevel(civ) < 1 || inCity(civ, q, r) || hasLaidRoad(civ, q, r)) return false;
  return villageOn(civ, q, r) || NEIGHBOURS.some(([dq, dr]) => onRoad(civ, q + dq, r + dr) || villageOn(civ, q + dq, r + dr));
};

/**
 * Where one order carries a unit along connected road from (q, r): up to
 * `level + 1` road hexes away, walking only on road. Empty unless the unit
 * starts on road. `ok(q, r)` is the caller's may-stand-here test.
 */
export const roadReach = (civ, q, r, ok = () => true) => {
  const level = roadLevel(civ);
  if (level < 1 || !onRoad(civ, q, r)) return [];
  const max = level + 1;
  const seen = new Map([[hexKey(q, r), 0]]);
  const queue = [[q, r, 0]];
  const out = [];
  while (queue.length) {
    const [cq, cr, d] = queue.shift();
    if (d >= max) continue;
    for (const [dq, dr] of NEIGHBOURS) {
      const nq = cq + dq; const nr = cr + dr;
      const k = hexKey(nq, nr);
      if (seen.has(k) || !onRoad(civ, nq, nr)) continue;
      seen.set(k, d + 1);
      queue.push([nq, nr, d + 1]);
      if (ok(nq, nr)) out.push([nq, nr]);
    }
  }
  return out;
};

/**
 * Is this hex joined to the city by road you can SEE: a laid road on it or
 * beside it, running unbroken over laid road to a hex that touches the city.
 * Touching the city is not enough by itself (playtest 12: the free road).
 */
export const joinedToCity = (civ, q, r) => {
  if (roadLevel(civ) < 1) return false;
  const start = [[q, r], ...NEIGHBOURS.map(([dq, dr]) => [q + dq, r + dr])].filter(([a, b]) => hasLaidRoad(civ, a, b));
  const seen = new Set(start.map(([a, b]) => hexKey(a, b)));
  const queue = [...start];
  while (queue.length) {
    const [cq, cr] = queue.shift();
    if (NEIGHBOURS.some(([dq, dr]) => inCity(civ, cq + dq, cr + dr))) return true;
    for (const [dq, dr] of NEIGHBOURS) {
      const nq = cq + dq; const nr = cr + dr;
      const k = hexKey(nq, nr);
      if (seen.has(k) || !hasLaidRoad(civ, nq, nr)) continue;
      if (hexDistanceBetween([nq, nr], [q, r]) > 30) continue;
      seen.add(k);
      queue.push([nq, nr]);
    }
  }
  return false;
};

/** What a joined village gains each night and per caravan: the road's level. */
export const roadBonus = (civ, v) => (v && joinedToCity(civ, v.q, v.r) ? roadLevel(civ) : 0);

/**
 * The road network as segments between hex centres, for drawing: every
 * road hex to each road or city neighbour, once. City-to-city is not drawn.
 */
export const roadSegments = (civ) => {
  const out = [];
  const done = new Set();
  for (const k of roadsOf(civ)) {
    const [q, r] = k.split(',').map(Number);
    for (const [dq, dr] of NEIGHBOURS) {
      const nq = q + dq; const nr = r + dr;
      if (!onRoad(civ, nq, nr)) continue;
      const id = [k, hexKey(nq, nr)].sort().join('|');
      if (done.has(id)) continue;
      done.add(id);
      out.push({ id, a: { q, r }, b: { q: nq, r: nr } });
    }
  }
  return out;
};
